import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceDot } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Lightbulb, Activity, Brain, Heart, Apple, Flame, Wind, Target, Circle, Triangle, Square, Diamond } from 'lucide-react';
import { useILScore } from '@/hooks/useILScore';
import { useMentalWellnessScore } from '@/hooks/useMentalWellnessScore';
import { useWhoopCorrelationData } from '@/hooks/useWhoopCorrelationData';
import { useChallengeCheckins, ChallengeCheckinEntry } from '@/hooks/useChallengeCheckins';
import { calculatePearsonCorrelation, formatCorrelationStrength, explainCorrelation } from '@/utils/statisticalCorrelation';
import { Skeleton } from '@/components/ui/skeleton';
import { StoredCorrelationSummaryCard } from './StoredCorrelationSummaryCard';
import { format, parseISO } from 'date-fns';

// Marker shapes for multi-challenge support
const CHALLENGE_MARKERS = ['●', '▲', '■', '◆'];
const CHALLENGE_COLORS = [
  'rgb(34, 197, 94)',   // green-500
  'rgb(59, 130, 246)',  // blue-500  
  'rgb(168, 85, 247)',  // purple-500
  'rgb(249, 115, 22)',  // orange-500
];

interface Factor {
  id: string;
  label: string;
  category: 'physical' | 'mental' | 'lifestyle' | 'recovery';
  icon: any;
  description: string;
  dataKey: string;
  source: 'il' | 'mental' | 'whoop';
}

const FACTORS: Factor[] = [
  // IL Score factors
  { id: 'diet', label: 'Diet Quality', category: 'lifestyle', icon: Apple, description: 'Whole foods vs processed foods intake', dataKey: 'diet_score', source: 'il' },
  { id: 'sleep', label: 'Sleep Quality', category: 'physical', icon: Activity, description: 'Sleep duration and quality', dataKey: 'sleep_score', source: 'il' },
  { id: 'stress', label: 'Stress Level', category: 'mental', icon: Brain, description: 'Daily stress and tension', dataKey: 'stress_score', source: 'il' },
  { id: 'activity', label: 'Physical Activity', category: 'physical', icon: Activity, description: 'Exercise and movement', dataKey: 'activity_score', source: 'il' },
  { id: 'alcohol', label: 'Alcohol Intake', category: 'lifestyle', icon: Flame, description: 'Alcohol consumption', dataKey: 'alcohol_score', source: 'il' },
  { id: 'smoking', label: 'Smoking', category: 'lifestyle', icon: Wind, description: 'Smoking habits', dataKey: 'smoking_score', source: 'il' },
  { id: 'hydration', label: 'Hydration', category: 'lifestyle', icon: Activity, description: 'Water intake', dataKey: 'hydration_score', source: 'il' },
  { id: 'social', label: 'Social Connection', category: 'mental', icon: Heart, description: 'Social interactions and relationships', dataKey: 'social_connection_score', source: 'il' },
  { id: 'il_score', label: 'Inflammation Load (IL)', category: 'physical', icon: Flame, description: 'Overall inflammation score', dataKey: 'il_score', source: 'il' },
  
  // Mental Wellness factors
  { id: 'mood', label: 'Mood', category: 'mental', icon: Brain, description: 'Overall emotional state', dataKey: 'mood_score', source: 'mental' },
  { id: 'energy', label: 'Energy Level', category: 'physical', icon: Activity, description: 'Mental and physical energy', dataKey: 'energy_score', source: 'mental' },
  { id: 'mind_clarity', label: 'Mental Clarity', category: 'mental', icon: Brain, description: 'Focus and cognitive function', dataKey: 'mind_clarity', source: 'mental' },
  { id: 'spiritual', label: 'Spiritual Wellbeing', category: 'mental', icon: Heart, description: 'Sense of purpose and connection', dataKey: 'spiritual_connection', source: 'mental' },
  
  // WHOOP factors
  { id: 'recovery', label: 'Recovery Score', category: 'recovery', icon: Activity, description: 'WHOOP recovery metric', dataKey: 'recovery_score', source: 'whoop' },
  { id: 'hrv', label: 'Heart Rate Variability', category: 'recovery', icon: Heart, description: 'HRV from WHOOP', dataKey: 'hrv', source: 'whoop' },
  { id: 'strain', label: 'Daily Strain', category: 'physical', icon: Activity, description: 'Physical exertion from WHOOP', dataKey: 'strain', source: 'whoop' },
  { id: 'rhr', label: 'Resting Heart Rate', category: 'physical', icon: Heart, description: 'RHR from WHOOP', dataKey: 'resting_heart_rate', source: 'whoop' },
];

interface CorrelationResult {
  factor: Factor;
  correlation: number;
  strength: string;
  direction: 'positive' | 'negative' | 'neutral';
  insight: string;
  advice: string;
}

export function CorrelationExplorer() {
  const [selectedFactorId, setSelectedFactorId] = useState<string>('');
  const [correlations, setCorrelations] = useState<CorrelationResult[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [showChallengeOverlays, setShowChallengeOverlays] = useState(true);
  
  const { data: ilData, isLoading: ilLoading } = useILScore(30);
  const { data: mentalData, isLoading: mentalLoading } = useMentalWellnessScore(30);
  const { data: whoopData, isLoading: whoopLoading, hasWhoopSubscription } = useWhoopCorrelationData(30);
  const { checkinMap, activeChallenges, isLoading: checkinsLoading } = useChallengeCheckins(30);

  const isLoading = ilLoading || mentalLoading || whoopLoading || checkinsLoading;
  
  // Create a challenge index map for consistent marker assignment
  const challengeIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    activeChallenges.forEach((c, idx) => {
      map.set(c.id, idx % CHALLENGE_MARKERS.length);
    });
    return map;
  }, [activeChallenges]);

  // Combine all data by date
  const combinedData = useMemo(() => {
    const dataMap = new Map();

    ilData?.forEach(item => {
      dataMap.set(item.date, { ...dataMap.get(item.date), ...item });
    });

    mentalData?.forEach(item => {
      dataMap.set(item.date, { ...dataMap.get(item.date), ...item });
    });

    whoopData?.forEach(item => {
      dataMap.set(item.date, { ...dataMap.get(item.date), ...item });
    });

    return Array.from(dataMap.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [ilData, mentalData, whoopData]);

  useEffect(() => {
    if (!selectedFactorId || combinedData.length < 7) return;

    const selectedFactor = FACTORS.find(f => f.id === selectedFactorId);
    if (!selectedFactor) return;

    const results: CorrelationResult[] = [];
    const selectedValues = combinedData
      .map(d => d[selectedFactor.dataKey])
      .filter(v => v !== null && v !== undefined)
      .reverse();

    // Calculate correlation with all other factors
    FACTORS.forEach(factor => {
      if (factor.id === selectedFactorId) return;
      
      // Note: All users can view all WHOOP metrics - no subscription gating on visualisation
      // WHOOP data availability is determined by whether data exists, not subscription status

      const factorValues = combinedData
        .map(d => d[factor.dataKey])
        .filter(v => v !== null && v !== undefined)
        .reverse();

      if (factorValues.length < 7 || selectedValues.length < 7) return;

      const minLength = Math.min(selectedValues.length, factorValues.length);
      const x = selectedValues.slice(0, minLength);
      const y = factorValues.slice(0, minLength);

      const result = calculatePearsonCorrelation(x, y);
      
      if (result.confidence === 'low') return;

      const direction = result.coefficient > 0 ? 'positive' : result.coefficient < 0 ? 'negative' : 'neutral';
      
      results.push({
        factor,
        correlation: result.coefficient,
        strength: formatCorrelationStrength(result.coefficient),
        direction,
        insight: generateInsight(selectedFactor, factor, result.coefficient, direction),
        advice: generateAdvice(selectedFactor, factor, direction),
      });
    });

    // Sort by correlation strength
    results.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
    setCorrelations(results);

    // Prepare trend data for selected factor with challenge overlay
    const trends = combinedData.slice(0, 14).reverse().map(d => {
      const dateStr = d.date;
      const checkinInfo = checkinMap[dateStr];
      return {
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: dateStr,
        value: d[selectedFactor.dataKey] || 0,
        challengeCompleted: checkinInfo?.completed ?? null,
        challengeTitle: checkinInfo?.challengeTitle || null,
        allChallenges: checkinInfo?.allChallenges || [],
      };
    });
    setTrendData(trends);
  }, [selectedFactorId, combinedData, hasWhoopSubscription, checkinMap]);

  const generateInsight = (primary: Factor, secondary: Factor, correlation: number, direction: string): string => {
    const absCorr = Math.abs(correlation);
    const strength = absCorr > 0.7 ? 'strong' : absCorr > 0.4 ? 'moderate' : 'weak';
    
    if (direction === 'positive') {
      return `When your ${primary.label.toLowerCase()} improves, your ${secondary.label.toLowerCase()} tends to improve as well. This ${strength} positive relationship suggests they work together to support your wellbeing.`;
    } else {
      return `When your ${primary.label.toLowerCase()} improves, your ${secondary.label.toLowerCase()} tends to decrease. This ${strength} inverse relationship is important for understanding your health patterns.`;
    }
  };

  const generateAdvice = (primary: Factor, secondary: Factor, direction: string): string => {
    const advice: Record<string, string> = {
      'diet-sleep': 'Focus on eating whole foods earlier in the day and avoiding processed foods 3-4 hours before bed for better sleep quality.',
      'diet-inflammation': 'Continue prioritising anti-inflammatory foods like leafy greens, berries, fatty fish, and nuts. Your diet is directly impacting your inflammation levels.',
      'stress-mood': 'Practice daily stress management techniques like meditation, deep breathing, or journaling to support your emotional wellbeing.',
      'activity-energy': 'Regular physical activity boosts energy. Aim for at least 30 minutes of movement daily, even if it\'s just a brisk walk.',
      'sleep-recovery': 'Quality sleep is essential for recovery. Maintain a consistent sleep schedule and create a relaxing bedtime routine.',
      'hydration-clarity': 'Your hydration levels affect cognitive function. Aim for 2-3 litres of water daily, starting with a glass upon waking.',
      'social-mood': 'Social connections significantly impact emotional health. Schedule regular time with friends and loved ones.',
      'alcohol-sleep': 'Alcohol disrupts sleep quality even in small amounts. Consider reducing intake, especially in the evening.',
    };

    const key = `${primary.id}-${secondary.id}`;
    return advice[key] || `Continue tracking both ${primary.label.toLowerCase()} and ${secondary.label.toLowerCase()} to understand their relationship better. Small consistent improvements in ${primary.label.toLowerCase()} can lead to meaningful changes in ${secondary.label.toLowerCase()}.`;
  };

  const getCorrelationColor = (correlation: number): string => {
    const abs = Math.abs(correlation);
    if (abs > 0.7) return 'text-chart-1';
    if (abs > 0.4) return 'text-chart-2';
    return 'text-chart-3';
  };

  const getCorrelationBadgeVariant = (direction: string): "default" | "secondary" | "outline" => {
    if (direction === 'positive') return 'default';
    if (direction === 'negative') return 'secondary';
    return 'outline';
  };

  const selectedFactor = FACTORS.find(f => f.id === selectedFactorId);

  const hasEnoughDataForExplorer = combinedData.length >= 7;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stored Correlation Summaries - ALWAYS show these regardless of real-time data */}
      <StoredCorrelationSummaryCard days={30} maxItems={5} />

      {/* Interactive Explorer - only show if we have enough data */}
      {hasEnoughDataForExplorer ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Interactive Explorer
            </CardTitle>
            <CardDescription>
              Dive deeper into specific factors to see how they relate to everything else you track
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Instructions */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground">How to use:</strong> Select any health factor from the dropdown below. 
                We'll show you a chart of that factor over time, plus a list of everything it correlates with. 
                This helps you discover which habits to focus on for maximum impact.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Select a factor to analyse</label>
              <Select value={selectedFactorId} onValueChange={setSelectedFactorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a health factor..." />
                </SelectTrigger>
                <SelectContent>
                  {/* All users can access all metric selectors including WHOOP factors */}
                  {FACTORS.map(factor => {
                    const Icon = factor.icon;
                    return (
                      <SelectItem key={factor.id} value={factor.id}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{factor.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2 border-muted">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-muted-foreground">
              <Lightbulb className="w-5 h-5" />
              Interactive Explorer
            </CardTitle>
            <CardDescription>Coming soon for your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-3 py-4">
              <p className="text-sm text-muted-foreground">
                Track for at least <strong>7 days</strong> to unlock the interactive correlation explorer.
              </p>
              <p className="text-xs text-muted-foreground/70">
                You currently have {combinedData.length} day{combinedData.length !== 1 ? 's' : ''} of data. 
                Keep logging your daily check-ins and this tool will unlock automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {hasEnoughDataForExplorer && selectedFactor && (
        <>
          {/* Trend Chart with Challenge Overlay */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {(() => {
                  const Icon = selectedFactor.icon;
                  return <Icon className="w-5 h-5" />;
                })()}
                {selectedFactor.label} Over Time
              </CardTitle>
              <CardDescription>{selectedFactor.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Challenge Overlay Toggle */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    id="challenge-overlay-toggle"
                    checked={showChallengeOverlays}
                    onCheckedChange={setShowChallengeOverlays}
                  />
                  <Label htmlFor="challenge-overlay-toggle" className="text-sm text-muted-foreground cursor-pointer">
                    Show challenge markers
                  </Label>
                </div>
              </div>

              {/* Challenge Legend - Dynamic based on active challenges */}
              {showChallengeOverlays && (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    These symbols show days where you completed or missed your personal challenges, so you can see how consistency interacts with your mood, sleep or IL patterns.
                  </p>
                  {activeChallenges.length > 0 ? (
                    <div className="mb-4 p-3 rounded-lg bg-muted/30 border border-border/50">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Target className="w-3.5 h-3.5 text-primary" />
                        <span className="text-xs font-medium text-foreground">Challenge Overlays</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {activeChallenges.map((challenge, idx) => {
                          const markerIdx = idx % CHALLENGE_MARKERS.length;
                          return (
                            <div key={challenge.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <span style={{ color: CHALLENGE_COLORS[markerIdx] }}>{CHALLENGE_MARKERS[markerIdx]}</span>
                              <span>{challenge.title} (completed)</span>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground/70 mt-2">Grey markers = missed check-in</p>
                    </div>
                  ) : (
                    <div className="mb-4 p-3 rounded-lg bg-muted/20 border border-border/30">
                      <p className="text-xs text-muted-foreground text-center">
                        No personal challenge data to overlay for this period yet.
                      </p>
                    </div>
                  )}
                </>
              )}
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0].payload;
                      const allChallenges: ChallengeCheckinEntry[] = data.allChallenges || [];
                      
                      return (
                        <div className="bg-card border border-border rounded-lg p-3 shadow-lg max-w-xs">
                          <p className="text-sm font-medium text-foreground mb-1">{label}</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedFactor?.label}: <span className="font-medium text-foreground">{data.value?.toFixed(1)}</span>
                          </p>
                          {allChallenges.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Challenge days:</p>
                              {allChallenges.map((c, i) => {
                                const markerIdx = challengeIndexMap.get(c.challengeId) ?? 0;
                                return (
                                  <div key={c.challengeId} className="flex items-center gap-1.5 text-xs">
                                    <span style={{ color: c.completed ? CHALLENGE_COLORS[markerIdx] : 'rgba(156,163,175,0.6)' }}>
                                      {CHALLENGE_MARKERS[markerIdx]}
                                    </span>
                                    <span className={c.completed ? 'text-foreground' : 'text-muted-foreground'}>
                                      {c.challengeTitle} - {c.completed ? 'completed' : 'missed'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={({ cx, cy, payload }) => {
                      const allChallenges: ChallengeCheckinEntry[] = payload.allChallenges || [];
                      
                      // Default dot style
                      const defaultDot = (
                        <circle 
                          key={`dot-${payload.fullDate}`}
                          cx={cx} 
                          cy={cy} 
                          r={4} 
                          fill="hsl(var(--primary))" 
                          stroke="hsl(var(--background))"
                          strokeWidth={2}
                        />
                      );
                      
                      // If overlays disabled or no challenge data, return default dot
                      if (!showChallengeOverlays || allChallenges.length === 0) {
                        return defaultDot;
                      }
                      
                      // Show overlay markers when enabled
                      return (
                        <g key={`challenge-dot-${payload.fullDate}`}>
                          {/* Main data dot */}
                          <circle 
                            cx={cx}
                            cy={cy} 
                            r={4} 
                            fill="hsl(var(--primary))" 
                            stroke="hsl(var(--background))"
                            strokeWidth={2}
                          />
                          {/* Challenge markers below the line - stack vertically for multi-challenge */}
                          {allChallenges.slice(0, 3).map((c, i) => {
                            const markerIdx = challengeIndexMap.get(c.challengeId) ?? 0;
                            const markerColor = c.completed 
                              ? CHALLENGE_COLORS[markerIdx]
                              : 'rgba(156, 163, 175, 0.5)';
                            const yOffset = 14 + (i * 8);
                            
                            // Use different shapes
                            if (markerIdx === 0) {
                              return (
                                <circle 
                                  key={`marker-${c.challengeId}`}
                                  cx={cx} 
                                  cy={cy + yOffset} 
                                  r={3.5} 
                                  fill={markerColor}
                                />
                              );
                            } else if (markerIdx === 1) {
                              return (
                                <polygon 
                                  key={`marker-${c.challengeId}`}
                                  points={`${cx},${cy + yOffset - 4} ${cx - 4},${cy + yOffset + 3} ${cx + 4},${cy + yOffset + 3}`}
                                  fill={markerColor}
                                />
                              );
                            } else if (markerIdx === 2) {
                              return (
                                <rect 
                                  key={`marker-${c.challengeId}`}
                                  x={cx - 3} 
                                  y={cy + yOffset - 3} 
                                  width={6} 
                                  height={6} 
                                  fill={markerColor}
                                />
                              );
                            } else {
                              return (
                                <polygon 
                                  key={`marker-${c.challengeId}`}
                                  points={`${cx},${cy + yOffset - 4} ${cx + 4},${cy + yOffset} ${cx},${cy + yOffset + 4} ${cx - 4},${cy + yOffset}`}
                                  fill={markerColor}
                                />
                              );
                            }
                          })}
                        </g>
                      );
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Correlation Strength Overview */}
          {correlations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Correlation Strengths</CardTitle>
                <CardDescription>
                  How strongly {selectedFactor.label.toLowerCase()} relates to other health factors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={correlations.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" domain={[-1, 1]} stroke="hsl(var(--muted-foreground))" />
                    <YAxis 
                      type="category" 
                      dataKey="factor.label" 
                      width={150}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar 
                      dataKey="correlation" 
                      fill="hsl(var(--primary))"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Correlation Insights */}
          {correlations.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Discovered Relationships</h3>
              {correlations.map((result, idx) => {
                const Icon = result.factor.icon;
                return (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{result.factor.label}</CardTitle>
                            <CardDescription>{result.factor.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getCorrelationBadgeVariant(result.direction)}>
                            {result.direction === 'positive' && <TrendingUp className="w-3 h-3 mr-1" />}
                            {result.direction === 'negative' && <TrendingDown className="w-3 h-3 mr-1" />}
                            {result.direction === 'neutral' && <Minus className="w-3 h-3 mr-1" />}
                            {result.strength}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Correlation Strength</span>
                          <span className={`text-sm font-bold ${getCorrelationColor(result.correlation)}`}>
                            {(result.correlation * 100).toFixed(0)}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.abs(result.correlation) * 100} 
                          className="h-2"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 mt-1 text-primary shrink-0" />
                          <div>
                            <p className="text-sm font-medium mb-1">What this means:</p>
                            <p className="text-sm text-muted-foreground">{result.insight}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5">
                          <Activity className="w-4 h-4 mt-1 text-primary shrink-0" />
                          <div>
                            <p className="text-sm font-medium mb-1">Action Steps:</p>
                            <p className="text-sm text-muted-foreground">{result.advice}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  Not enough data yet to calculate correlations with {selectedFactor.label.toLowerCase()}. 
                  Keep tracking consistently for more insights!
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedFactor && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Select a health factor above to begin exploring correlations and discover how different aspects 
              of your life influence each other.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
